import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APPLICATION_FIELD_LABELS } from '@lmc-app/shared/constants/application-field-labels.constant';

/**
 * Helper function to convert picklist array to Formly options format
 */
export function toFormlyOptions(items: Observable<any[]>, valueProp = 'display_value', labelProp = 'display_value') {
  return items.pipe(
    map((list) =>
      list.map((item) => ({
        value: item[valueProp],
        label: item[labelProp],
      }))
    )
  );
}

/**
 * Name pattern validator
 */
export const namePattern = /^(?!\d)(?!.*\d)(?![^a-zA-Z\s]).*(?<![^a-zA-Z\s])$/;

/**
 * Helper function to check if lead source is Rapid Program
 */
export function isRapidProgram(field: FormlyFieldConfig): boolean {
  const leadSource = field.model?.Lead_Source;
  return leadSource === 'Rapid Program - Portal' || leadSource === 'Rapid Program - Internal';
}

/**
 * Helper function to check if stage is open
 */
export function isBCTRequiredStage(field: FormlyFieldConfig): boolean {
  let isBCTRequired: boolean = false;
  const stage = field.model?._stage;
  switch (stage) {
    case "Rapid - Underwriting":
    case "Co-App Budget Assessment":
    case "Budget Assessment":
      isBCTRequired = true;
      break;
    default:
      break;
  }
  return isBCTRequired;
}

/**
 * Find the section label for a given field key
 * Searches through the Formly fields configuration to locate the parent section
 */
export function findFieldSection(fields: FormlyFieldConfig[], fieldKey: string): string | null {
  // Search through all expansion panels (sections)
  for (const section of fields) {
    // Check if this is an expansion panel with a label
    if (section.fieldGroup?.filter(e => e.type === "section-header")[0].props?.label) {
      const sectionLabel = section.fieldGroup?.filter(e => e.type === "section-header")[0].props?.label;

      // Search within the section's fieldGroup
      if (searchFieldInGroup(section.fieldGroup, fieldKey)) {
        return sectionLabel;
      }
    }
  }
  return null;
}

/**
 * Recursively search for a field key within a field group
 */
function searchFieldInGroup(
  fieldGroup: FormlyFieldConfig[] | undefined,
  targetKey: string
): boolean {
  if (!fieldGroup) return false;

  for (const field of fieldGroup) {
    // Check if this field matches the target key
    if (field.key === targetKey) {
      return true;
    }

    // Recursively search in nested fieldGroups
    if (field.fieldGroup && searchFieldInGroup(field.fieldGroup, targetKey)) {
      return true;
    }

    // Search in fieldArray
    if (field.fieldArray) {
      const fieldArray =
        typeof field.fieldArray === 'function'
          ? field.fieldArray(field)
          : field.fieldArray;

      if (fieldArray.fieldGroup && searchFieldInGroup(fieldArray.fieldGroup, targetKey)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get field label with section context
 * Returns label in format: "Label (Section)" for fields that need section context
 */
export function getFieldLabelWithSection(
  fields: FormlyFieldConfig[],
  fieldKey: string
): string {
  const section = findFieldSection(fields, fieldKey);
  const baseLabel = APPLICATION_FIELD_LABELS[fieldKey] || fieldKey;
  return section ? `${baseLabel} (${section})` : baseLabel;
}

/**
 * Get 10 digit number as string
 * Returns string in phone format: "(XXX) XXX-XXXX", otherwise return as input
 */
export function getPhoneFormat(num: string): string {
  const match = num.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : num;
};